import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { AdminBlogPost } from "@handvero/shared";
import { api, errorText } from "../api";
import { Prose } from "../components/Prose";
import { Message } from "./ui";

/** Blog verwalten: schreiben, veröffentlichen und, nur hier, auf LinkedIn und Instagram teilen. */

const EMPTY = { id: "", title: "", excerpt: "", body: "", published: false };
type Draft = typeof EMPTY;

/** Bild im Hochformat (1080 x 1350) für Instagram, gezeichnet im Browser, ohne Fremddienst. */
function shareCard(title: string, url: string): Promise<Blob | null> {
  const w = 1080;
  const h = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const c = canvas.getContext("2d");
  if (!c) return Promise.resolve(null);
  const grad = c.createLinearGradient(0, 0, w, h);
  grad.addColorStop(0, "#1E3863");
  grad.addColorStop(1, "#0E1F3F");
  c.fillStyle = grad;
  c.fillRect(0, 0, w, h);
  c.fillStyle = "#F2B84B";
  c.beginPath();
  c.arc(w - 150, 190, 70, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = "rgba(157,183,227,0.35)";
  c.beginPath();
  c.moveTo(0, h - 330);
  c.bezierCurveTo(240, h - 470, 520, h - 300, w, h - 430);
  c.lineTo(w, h);
  c.lineTo(0, h);
  c.fill();
  c.fillStyle = "rgba(47,158,98,0.85)";
  c.beginPath();
  c.moveTo(0, h - 160);
  c.bezierCurveTo(300, h - 230, 700, h - 130, w, h - 200);
  c.lineTo(w, h);
  c.lineTo(0, h);
  c.fill();
  c.fillStyle = "#fff";
  c.font = "700 34px Roboto, system-ui, sans-serif";
  c.fillText("HandVero", 80, 110);
  c.font = "900 84px Roboto, system-ui, sans-serif";
  const words = title.split(/\s+/);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (c.measureText(next).width > w - 160 && line) {
      lines.push(line);
      line = word;
    } else line = next;
  }
  if (line) lines.push(line);
  lines.slice(0, 7).forEach((l, i) => c.fillText(l, 80, 380 + i * 100));
  c.font = "500 34px Roboto, system-ui, sans-serif";
  c.fillStyle = "#CFDDF2";
  c.fillText(url.replace(/^https?:\/\//, ""), 80, h - 70);
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export function BlogAdmin() {
  const [posts, setPosts] = useState<AdminBlogPost[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [slug, setSlug] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(() => {
    api.get<AdminBlogPost[]>("/api/admin/blog").then(setPosts, (e) => setMsg({ ok: false, text: errorText(e) }));
  }, []);
  useEffect(load, [load]);

  function open(p: AdminBlogPost | null) {
    setMsg(null);
    setPreview(false);
    setSlug(p?.slug ?? "");
    setDraft(p ? { id: p.id, title: p.title, excerpt: p.excerpt, body: p.body, published: p.published } : { ...EMPTY });
  }

  async function save(e: FormEvent, publish?: boolean) {
    e.preventDefault();
    if (!draft) return;
    setBusy(true);
    setMsg(null);
    const payload = { title: draft.title, excerpt: draft.excerpt, body: draft.body, published: publish ?? draft.published };
    try {
      const saved = draft.id ? await api.put<AdminBlogPost>(`/api/admin/blog/${draft.id}`, payload) : await api.post<AdminBlogPost>("/api/admin/blog", payload);
      setDraft({ id: saved.id, title: saved.title, excerpt: saved.excerpt, body: saved.body, published: saved.published });
      setSlug(saved.slug);
      setMsg({ ok: true, text: saved.published ? "Gespeichert und veröffentlicht." : "Als Entwurf gespeichert. Auf der Website sieht ihn niemand." });
      load();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!draft?.id || !window.confirm("Diesen Beitrag endgültig löschen? Geteilte Links funktionieren dann nicht mehr.")) return;
    try {
      await api.del(`/api/admin/blog/${draft.id}`);
      setDraft(null);
      load();
    } catch (err) {
      setMsg({ ok: false, text: errorText(err) });
    }
  }

  if (!posts) return msg ? <Message>{msg.text}</Message> : <div className="skel-block" />;

  if (!draft) {
    return (
      <div className="adm">
        <p className="adm-source">Beiträge erscheinen auf der Website unter „Blog“, sobald Sie sie veröffentlichen. Teilen auf LinkedIn und Instagram geht nur hier.</p>
        <button className="app-btn" onClick={() => open(null)}>
          Neuer Beitrag
        </button>
        <ul className="adm-bloglist">
          {posts.map((p) => (
            <li key={p.id}>
              <button className="btn-text" onClick={() => open(p)}>
                <b>{p.title}</b>
              </button>
              <span className={`adm-pill ${p.published ? "active" : ""}`}>{p.published ? "Veröffentlicht" : "Entwurf"}</span>
            </li>
          ))}
          {posts.length === 0 && <li className="muted-s">Noch kein Beitrag. Schreiben Sie den ersten.</li>}
        </ul>
        {msg && <Message>{msg.text}</Message>}
      </div>
    );
  }

  const url = slug ? `${window.location.origin}/blog/${slug}` : "";
  return (
    <div className="adm adm-blog">
      <button className="btn-text" onClick={() => setDraft(null)}>
        ← Alle Beiträge
      </button>
      <form className="app-form" onSubmit={(e) => void save(e)}>
        <label>
          Überschrift
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} maxLength={140} required />
        </label>
        <label>
          Teaser (kurzer Anreißer für Liste und Teilen)
          <textarea rows={2} value={draft.excerpt} onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })} maxLength={300} />
        </label>
        <label>
          Text
          <textarea rows={16} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} required />
        </label>
        <p className="muted-s">
          Schreibweise: Leerzeile = neuer Absatz. „## Überschrift“, „### Zwischenüberschrift“, „- Listenpunkt“, „&gt; Zitat“, „**fett**“, „[Text](https://…)“.
        </p>
        <div className="adm-actions">
          <button className="app-btn" disabled={busy}>
            Speichern
          </button>
          {!draft.published ? (
            <button type="button" className="app-btn" disabled={busy} onClick={(e) => void save(e, true)}>
              Veröffentlichen
            </button>
          ) : (
            <button type="button" className="btn-text" disabled={busy} onClick={(e) => void save(e, false)}>
              Zurück auf Entwurf
            </button>
          )}
          <button type="button" className="btn-text" onClick={() => setPreview((v) => !v)}>
            {preview ? "Vorschau schließen" : "Vorschau"}
          </button>
          {draft.id && (
            <button type="button" className="btn-text" onClick={() => void remove()}>
              Löschen
            </button>
          )}
        </div>
        {msg && (msg.ok ? <p className="adm-ok-inline">{msg.text}</p> : <Message>{msg.text}</Message>)}
      </form>
      {preview && (
        <div className="adm-preview">
          <h2>{draft.title}</h2>
          <Prose text={draft.body} />
        </div>
      )}
      {draft.published && url && <SharePanel title={draft.title} excerpt={draft.excerpt} url={url} />}
    </div>
  );
}

function SharePanel({ title, excerpt, url }: { title: string; excerpt: string; url: string }) {
  const [caption, setCaption] = useState(`${title}\n\n${excerpt ? `${excerpt}\n\n` : ""}${url}\n\n#HandVero #Handwerk #Erzgebirge`);
  const [hint, setHint] = useState("");
  useEffect(() => setCaption(`${title}\n\n${excerpt ? `${excerpt}\n\n` : ""}${url}\n\n#HandVero #Handwerk #Erzgebirge`), [title, excerpt, url]);

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      setHint(`${what} kopiert.`);
    } catch {
      setHint("Kopieren hat nicht geklappt. Bitte den Text von Hand markieren.");
    }
  }
  async function image() {
    const blob = await shareCard(title, url);
    if (!blob) return setHint("Das Bild konnte nicht erstellt werden.");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "handvero-beitrag.png";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    setHint("Bild gespeichert.");
  }

  return (
    <div className="adm-share">
      <h3>Teilen (nur für Sie)</h3>
      <p className="muted-s">Es öffnet sich jeweils das Netzwerk in einem neuen Fenster. Dort prüfen und senden Sie selbst ab. HandVero postet nichts allein.</p>
      <div className="adm-actions">
        <a className="app-btn" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer">
          Auf LinkedIn teilen
        </a>
        <button type="button" className="app-btn" onClick={() => void image()}>
          Bild für Instagram speichern
        </button>
        <a className="btn-text" href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer">
          Instagram öffnen
        </a>
        <button type="button" className="btn-text" onClick={() => void copy(url, "Link")}>
          Link kopieren
        </button>
      </div>
      <label>
        Text für den Beitrag
        <textarea rows={6} value={caption} onChange={(e) => setCaption(e.target.value)} />
      </label>
      <button type="button" className="btn-text" onClick={() => void copy(caption, "Text")}>
        Text kopieren
      </button>
      {hint && <p className="adm-ok-inline">{hint}</p>}
    </div>
  );
}
